import React, {useEffect, useState} from "react";
import {Certifications} from "../types";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {CertificationAPI} from "../services/API/CertificationAPI";
import {UtilityForm} from "../utils/UtilityForm";
import {useLocale} from "../contexts/LocaleContext";
import {useTranslation} from "react-i18next";
import {Translate as TranslateIcon} from "@mui/icons-material";
import {CertificationTranDTO} from "../types/DTOs/Translation/CertificationTranDTO";
import {Translate} from "../utils/Translate";
import {TranslationAPI} from "../services/API/TranslationAPI";
import {SectionTypes} from "../utils/enums";

// ─────────────────────────────────────────────────────────────────────────────
// Lingua di default (originale)
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_LANG = 'original';

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────
interface EditCertificationProps {
    item: Certifications | null;
    onSave: () => void;
    onCancel: () => void;
    open: boolean;
    onClose: () => void;
    /** Lingue già tradotte per questo elemento (chip nella lista) */
    translatedLanguages?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// MOCK API — sostituisci con le chiamate reali
// ─────────────────────────────────────────────────────────────────────────────

/** Lingue disponibili per la traduzione.
 *  GET /api/translations/available-languages
 *  che restituisce { code: string, name: string }[]
 */
const fetchAvailableLanguages = async (): Promise<{ code: string; i18nKey: string }[]> => {
    return Translate.LanguageForCombo();
};

/** Carica la traduzione di una certificazione per una lingua.
 *  GET /api/translations/certification/{certificationId}/{languageCode}
 *  che restituisce CertificationTranDTO | null
 */
const fetchCertificationTranslation = async (
    certificationId: string,
    languageCode: string
): Promise<CertificationTranDTO | null | undefined> => {
    let dataTran = await TranslationAPI.Instance<TranslationAPI>().getTranslatedItem(certificationId, SectionTypes.CERTIFICATION, languageCode);
    if (dataTran === null || dataTran === undefined) {
        return null;
    }
    return dataTran as CertificationTranDTO;
};

/** Salva la traduzione di una certificazione.
 *  POST /api/translations/certification  (se nuovo)
 *  PUT  /api/translations/certification/{id}  (se aggiornamento)
 *  body: CertificationTranDTO
 */
const saveCertificationTranslation = async (
    data: CertificationTranDTO
): Promise<void> => {
    await TranslationAPI.Instance<TranslationAPI>().saveTranslateItem(data, SectionTypes.CERTIFICATION);
    //console.log('Salvataggio traduzione:', data);
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────
export const EditCertification: React.FC<EditCertificationProps> = ({
                                                                        item,
                                                                        onSave,
                                                                        onCancel,
                                                                        open,
                                                                        onClose,
                                                                        translatedLanguages = [],
                                                                    }) => {
    // ── Stato originale ──
    const [certification, setCertification] = useState<Certifications>({
        id: '',
        userId: '',
        certName: '',
        issuer: '',
        dateObtained: new Date(),
        description: '',
        createdAt: '',
        updatedAt: ''
    });

    // ── Stato traduzione ──
    const [availableLanguages, setAvailableLanguages] = useState<{ code: string; i18nKey: string }[]>([]);
    const [selectedLang, setSelectedLang] = useState<string>(DEFAULT_LANG);
    const [translation, setTranslation] = useState<CertificationTranDTO>({
        id: '',
        certificationId: '',
        certName: '',
        description: '',
    });
    const [translationLoading, setTranslationLoading] = useState(false);
    const [translationError, setTranslationError] = useState<string>('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const isTranslationMode = selectedLang !== DEFAULT_LANG;

    const { t } = useTranslation();
    const { locale } = useLocale();

    // ─────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            // Reset lingua alla riapertura
            setSelectedLang(DEFAULT_LANG);
            setTranslationError('');
            setSaveSuccess(false);

            if (item === null) {
                setCertificationDefault();
            } else {
                setCertification(item);
            }

            // Carica lingue disponibili
            fetchAvailableLanguages()
                .then(setAvailableLanguages)
                .catch(() => {});
        }
    }, [item, open]);

    // Quando cambia lingua, carica la traduzione
    useEffect(() => {
        if (!isTranslationMode || !certification.id) return;

        setTranslationLoading(true);
        setTranslationError('');
        setSaveSuccess(false);

        fetchCertificationTranslation(certification.id, selectedLang)
            .then(data => {
                if (data) {
                    setTranslation(data);
                } else {
                    // Nessuna traduzione esistente: campi vuoti pronti per inserimento manuale
                    setTranslation({
                        id: '',
                        certificationId: certification.id,
                        certName: '',
                        description: '',
                    });
                }
            })
            .catch(() => setTranslationError(t('translation.errorGeneric')))
            .finally(() => setTranslationLoading(false));
    }, [selectedLang]);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────
    const setCertificationDefault = () => {
        setCertification({
            id: '', userId: '', certName: '', issuer: '',
            dateObtained: new Date(), description: '', createdAt: '', updatedAt: ''
        });
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setCertification(prev => ({ ...prev, [name]: value }));
    };

    const handleTranslationInputChange = (event: any) => {
        const { name, value } = event.target;
        setTranslation(prev => ({ ...prev, [name]: value }));
    };

    const handleLangChange = (e: SelectChangeEvent) => {
        setSelectedLang(e.target.value);
        setSaveSuccess(false);
    };

    // Salva originale
    const handleSaveItem = async () => {
        try {
            if (!certification.id) {
                await CertificationAPI.Instance<CertificationAPI>().createCertifications({
                    certName: certification.certName,
                    issuer: certification.issuer,
                    dateObtained: certification.dateObtained,
                    expirationDate: certification.expirationDate ?? null,
                    description: certification.description
                });
            } else {
                await CertificationAPI.Instance<CertificationAPI>().updateCertifications(certification.id, {
                    certName: certification.certName,
                    issuer: certification.issuer,
                    dateObtained: certification.dateObtained,
                    expirationDate: certification.expirationDate ?? null,
                    description: certification.description
                });
            }
            setCertificationDefault();
            onSave();
        } catch (error) {
            console.error('Error saving certification:', error);
        }
    };

    // Salva traduzione (non chiude la dialog)
    const handleSaveTranslation = async () => {
        setSaveLoading(true);
        setSaveSuccess(false);
        setTranslationError('');
        try {
            await saveCertificationTranslation({
                ...translation,
                certificationId: certification.id,
            });
            setSaveSuccess(true);
        } catch {
            setTranslationError(t('translation.errorGeneric'));
        } finally {
            setSaveLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────{t(lang.i18nKey)}
    const langLabel = availableLanguages.find(l => l.code === selectedLang)?.i18nKey ?? '';

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>

            {/* ── Titolo ── */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{t('editDialog.title')}</span>
                    {isTranslationMode && (
                        <Chip
                            icon={<TranslateIcon />}
                            label={t(langLabel)}
                            color="primary"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </DialogTitle>

            {/* ── Banda lingua — separata dal contenuto ── */}
            {certification.id && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('translation.selectLanguageLabel')}</InputLabel>
                        <Select
                            value={selectedLang}
                            label={t('translation.selectLanguageLabel')}
                            onChange={handleLangChange}
                            startAdornment={<TranslateIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />}
                        >
                            <MenuItem value={DEFAULT_LANG}>
                                <em>{t('translation.originalLanguage')}</em>
                            </MenuItem>
                            <Divider />
                            {availableLanguages.map(lang => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {t(lang.i18nKey)}
                                        {translatedLanguages.includes(lang.code) && (
                                            <Chip
                                                label={t('translation.alreadyTranslatedChip')}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isTranslationMode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {translatedLanguages.includes(selectedLang)
                                ? t('translation.editModeExisting')
                                : t('translation.editModeNew')}
                        </Typography>
                    )}
                </Box>
            )}

            <DialogContent  sx={{ pt: 2 }}>

                {/* ── Loading traduzione ── */}
                {translationLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {/* ── Errore traduzione ── */}
                {translationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{translationError}</Alert>
                )}

                {/* ── Feedback salvataggio traduzione ── */}
                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>{t('translation.saveSuccess')}</Alert>
                )}

                {!translationLoading && (
                    <>
                        {/* ── Nome certificazione ── */}
                        <TextField
                            name="certName"
                            margin="dense"
                            label={t('editDialog.name')}
                            fullWidth
                            variant="outlined"
                            // In modalità traduzione: campo traducibile → usa stato translation
                            value={isTranslationMode ? translation.certName : certification.certName}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            sx={{ mb: 2 }}
                        />

                        {/* ── Ente certificatore — NON traducibile ── */}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <TextField
                                name="issuer"
                                margin="dense"
                                label={t('editDialog.issuer')}
                                fullWidth
                                variant="outlined"
                                value={certification.issuer}
                                onChange={handleInputChange}
                                disabled={isTranslationMode}
                                sx={{ mb: 2 }}
                            />
                        </Tooltip>

                        {/* ── Date — NON traducibili ── */}
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                            <Tooltip
                                title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                                placement="top-start"
                            >
                                <span style={{ display: 'block', marginBottom: 8 }}>
                                    <DatePicker
                                        name="dateObtained"
                                        label={t('editDialog.obtainedDate')}
                                        value={dayjs(certification.dateObtained)}
                                        onChange={(date) => UtilityForm.handleDateChange('dateObtained', date, setCertification)}
                                        disabled={isTranslationMode}
                                        sx={{ width: '100%' }}
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                                placement="top-start"
                            >
                                <span style={{ display: 'block', marginBottom: 8 }}>
                                    <DatePicker
                                        name="expirationDate"
                                        label={t('editDialog.expireDate')}
                                        value={certification.expirationDate ? dayjs(certification.expirationDate) : null}
                                        onChange={(date) => UtilityForm.handleDateChange('expirationDate', date, setCertification)}
                                        disabled={isTranslationMode}
                                        sx={{ width: '100%' }}
                                    />
                                </span>
                            </Tooltip>
                        </LocalizationProvider>

                        {/* ── Descrizione — traducibile ── */}
                        <TextField
                            name="description"
                            margin="dense"
                            label={t('editDialog.description')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.description : certification.description}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                        />
                    </>
                )}

            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('standardUse.undoBtn')}
                </Button>

                {isTranslationMode ? (
                    // ── Salva traduzione (non chiude) ──
                    <Button
                        onClick={handleSaveTranslation}
                        variant="contained"
                        color="primary"
                        disabled={saveLoading || translationLoading}
                        startIcon={saveLoading
                            ? <CircularProgress size={16} color="inherit" />
                            : <TranslateIcon />
                        }
                    >
                        {saveLoading
                            ? t('translation.savingButton')
                            : t('translation.saveTranslationButton', { lang: langLabel })}
                    </Button>
                ) : (
                    // ── Salva originale (chiude) ──
                    <Button onClick={handleSaveItem} variant="contained">
                        {t('standardUse.saveBtn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};